<script lang="ts" setup>
import { computed, nextTick, onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { GenderEnum } from "@/enums/mateEnums";
import { useGlobalUser } from "@/stores/globalUser";
import { useMate } from "@/stores/mate";
import { Popup, showDialog, showLoadingToast } from "vant";
import RegisterAppearanceUpload from "@/components/mobile/login/create-mate/RegisterAppearanceUpload.vue";
import RegisterAppearanceGenerate from "@/components/mobile/login/create-mate/RegisterAppearanceGenerate.vue";
import RegisterEmail from "@/components/mobile/login/create-mate/RegisterEmail.vue";
import useLogin from "@/components/mobile/login/useLogin";
import { userCompleteSignup } from "@/apis/user";
import { useStaticUrl } from "@/use/common";
import { buildRedrawParams } from "@/components/mobile/login/util";
import api from "@/modules/api";
import { RadialProgressPopupInstance } from "@/components/mobile/login/create-mate/RadialProgressPopup.vue";

const { t } = useI18n();
const router = useRouter();
const userStore = useGlobalUser();
const mateStore = useMate();

enum StepEnum {
  genderSelection = 0,
  appearanceUpload = 1,
  appearanceGenerate = 2,
  nameSetting = 3,
  register = 4,
  welcome = 5,
}

const currentStep = ref<StepEnum>(StepEnum.genderSelection);
const gender = ref<GenderEnum | null>(null);
const referenceMedia = ref<Pick<Media, "id" | "url"> | null>(null);
const generatedMedia = ref<Media | null>(null);
const hasNotSelectedGender = computed(() => gender.value === null);
const mateName = ref("");
const stylePopupShow = ref(false);
const selectedStyleId = ref<number | undefined>();
const { email, verifyCode, nextResendTime, sendVerificationCode } = useLogin();
const showProgress = ref(false);
const progressRef = ref<RadialProgressPopupInstance | null>(null);
// Lifecycle
onBeforeMount(() => {
  gender.value = mateStore.currentMate?.gender ?? null;
  mateName.value = mateStore.currentMate?.name || "";
  generatedMedia.value = mateStore.currentMate?.mediaId
    ? ({
        id: mateStore.currentMate?.mediaId,
        url: mateStore.currentMate?.mediaUrl,
      } as Media)
    : null;
  // 1. 登录 && 有邮箱 => 完成注册步骤，跳转到主页
  if (userStore.isLogin) {
    currentStep.value = StepEnum.welcome;
  } else if (userStore.id && !userStore.email && mateStore.currentMate) {
    // 2. 登录 && 无邮箱
    if (mateStore.currentMate?.name) {
      currentStep.value = StepEnum.register;
    } else if (mateStore.currentMate?.mediaUrl) {
      // 2.1. 有头像 => 进入name设置
      currentStep.value = StepEnum.nameSetting;
    } else {
      // 2.2. 无头像 => 进入形象设置
      gender.value = mateStore.currentMate!.gender as GenderEnum;
      currentStep.value = StepEnum.appearanceUpload;
    }
  } else {
    // 3. 未登录 => 进入gender选择
    // do nothing
  }
});

// Methods
const goPrevStep = async () => {
  if (currentStep.value === StepEnum.genderSelection) {
    // do nothing
  } else {
    currentStep.value--;
  }
};
const goNextStep = async () => {
  if (currentStep.value === StepEnum.genderSelection) {
    // 1. 创建User和Mate
    if (gender.value === null) {
      gender.value = GenderEnum.NOT_SET;
    }
    const loading = showLoadingToast("");
    try {
      const currentMate = mateStore.currentMate;
      if (currentMate) {
        // 1.1 有mate，更新mate的gender
        await mateStore.updateMateDetail(currentMate.uuid, {
          gender: gender.value!,
        });
      } else {
        // 1.2 无mate，创建mate和user
        await userStore.anonymousUserInit({ gender: gender.value! });
      }
      currentStep.value++;
    } finally {
      loading.close();
    }
  } else if (currentStep.value === StepEnum.appearanceGenerate) {
    const loading = showLoadingToast("");
    try {
      await mateStore.updateMateDetail(mateStore.currentMate!.uuid, {
        mediaUrl: generatedMedia.value!.url,
        mediaId: generatedMedia.value!.id,
      });
      currentStep.value++;
    } finally {
      loading.close();
    }
  } else if (currentStep.value === StepEnum.nameSetting) {
    const loading = showLoadingToast("");
    try {
      const currentMate = mateStore.currentMate;
      await mateStore.updateMateDetail(currentMate!.uuid, {
        name: mateName.value,
      });
      currentStep.value++;
    } finally {
      loading.close();
    }
  } else if (currentStep.value === StepEnum.register) {
    const loading = showLoadingToast("");
    try {
      await userCompleteSignup({
        email: email.value,
        token: Number(verifyCode.value),
        name: mateName.value,
        mediaId: mateStore.currentMate!.mediaId,
      });
      currentStep.value++;
    } finally {
      loading.close();
    }
  } else if (currentStep.value === StepEnum.welcome) {
    // do nothing
  } else {
    currentStep.value++;
  }
};
const isNextStepDisabled = computed(() => {
  if (currentStep.value === StepEnum.genderSelection) {
    // 0 || null => disabled
    return !gender.value;
  } else if (currentStep.value === StepEnum.appearanceGenerate) {
    return !generatedMedia.value && !mateStore.currentMate?.mediaUrl;
  } else if (currentStep.value === StepEnum.nameSetting) {
    return mateName.value.length === 0;
  } else if (currentStep.value === StepEnum.register) {
    return email.value.length === 0 || verifyCode.value.length === 0;
  } else if (currentStep.value === StepEnum.welcome) {
    return false;
  }
  return false;
});
const onGenerate = async () => {
  showProgress.value = true;
  const param = buildRedrawParams({
    referenceMediaId: referenceMedia.value?.id,
    gender: gender.value!,
  });
  param.style = selectedStyleId.value!;
  try {
    const res = await api.createOneTextImage(param);
    generatedMedia.value = res.item;
    stylePopupShow.value = false;
    progressRef.value?.finish();
    await nextTick(() => {
      currentStep.value = StepEnum.appearanceGenerate;
    });
  } finally {
    progressRef.value?.finish();
  }
};
const onAvatarClick = () => {
  showDialog({
    title: t("loginmodal.avatarClickTipTitle"),
    message: t("loginmodal.avatarClickTip"),
    confirmButtonText: t("Buttons.confirm"),
  });
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr] relative register-container">
    <div class="navbar bg-base-100 h-top-nav bg-transparent relative z-20">
      <!-- 左 -->
      <div class="navbar-start h-full">
        <button
          v-if="currentStep !== StepEnum.welcome"
          class="btn btn-sm btn-ghost text-base-content/50"
          @click="router.back()"
        >
          {{ $t("Buttons.back") }}
        </button>
      </div>
      <!-- 中 -->
      <div class="navbar-center"></div>
      <!-- 右: 占位 -->
      <div class="navbar-end">
        <button
          v-if="currentStep !== StepEnum.welcome"
          class="btn btn-sm btn-ghost text-base-content/50"
          @click="$router.push({ name: 'login', query: { mode: 'login' } })"
        >
          {{ $t("loginmodal.skip") }}
        </button>
      </div>
    </div>
    <div
      :class="{ grayscale: hasNotSelectedGender }"
      :style="{
        backgroundImage: `url(${useStaticUrl(
          '/login/avatar-wall.png'
        )}?x-oss-process=image/format,webp)`,
      }"
      class="absolute left-0 right-0 top-0 bottom-0 blur-[2px] z-0 register-container-bg bg-repeat"
    />
    <div
      class="absolute left-0 right-0 top-0 bottom-0 bg-base-100/80 z-10"
    ></div>
    <div
      class="h-full w-full relative z-20 flex flex-col items-center gap-4 p-4 justify-evenly"
    >
      <LogoIcon class="w-36" />

      <RegisterGenderSelection
        v-if="currentStep === StepEnum.genderSelection"
        v-model:gender="gender"
        class="flex-grow"
      />
      <RegisterAppearanceUpload
        v-else-if="currentStep === StepEnum.appearanceUpload"
        v-model:generated-media="generatedMedia"
        v-model:reference-media="referenceMedia"
        :gender="gender!"
      />
      <RegisterAppearanceGenerate
        v-else-if="currentStep === StepEnum.appearanceGenerate"
        v-model:generated-media="generatedMedia"
        v-model:reference-media="referenceMedia"
        :gender="gender!"
        @regenerate="onGenerate"
        @show-style-selection="stylePopupShow = true"
        @on-avatar-click="onAvatarClick"
      />
      <RegisterMateName
        v-else-if="currentStep === StepEnum.nameSetting"
        v-model:name="mateName"
        @on-avatar-click="onAvatarClick"
      />
      <RegisterEmail
        v-else-if="currentStep === StepEnum.register"
        v-model:email="email"
        v-model:next-resend-time="nextResendTime"
        v-model:verify-code="verifyCode"
        :send-verification-code="sendVerificationCode"
        @on-avatar-click="onAvatarClick"
      />
      <RegisterWelcome v-else-if="currentStep === StepEnum.welcome" />
      <div v-if="currentStep !== StepEnum.welcome" class="flex w-full gap-4">
        <button
          v-if="currentStep !== StepEnum.genderSelection"
          class="btn flex-1 btn-neutral disabled:bg-secondary disabled:text-white rounded btn-sm md:btn-md"
          @click="goPrevStep"
        >
          {{ $t("loginmodal.prevStep") }}
        </button>
        <button
          v-if="currentStep === StepEnum.genderSelection"
          class="btn flex-1 btn-neutral disabled:bg-secondary disabled:text-white rounded btn-sm md:btn-md"
          @click="
            gender = GenderEnum.NOT_SET;
            goNextStep();
          "
        >
          {{ $t("loginmodal.notSetGender") }}
        </button>
        <button
          v-if="currentStep !== StepEnum.appearanceUpload"
          :disabled="isNextStepDisabled"
          class="btn flex-1 btn-primary disabled:bg-secondary disabled:text-white rounded btn-sm md:btn-md"
          @click="goNextStep"
        >
          {{ $t("loginmodal.nextStep") }}
        </button>
        <button
          v-if="currentStep === StepEnum.appearanceUpload && referenceMedia"
          class="btn flex-1 btn-primary disabled:bg-secondary disabled:text-white rounded btn-sm md:btn-md"
          @click="stylePopupShow = true"
        >
          {{ $t("loginmodal.selectStyle") }}
        </button>
      </div>
      <app-footer />
    </div>
    <popup
      v-model:show="stylePopupShow"
      class="h-[90%] grid grid-rows-[auto_1fr]"
      position="bottom"
    >
      <div class="flex justify-between p-2">
        <button
          class="btn btn-neutral btn-sm rounded"
          @click="stylePopupShow = false"
        >
          {{ $t("Buttons.back") }}
        </button>
        <button class="btn btn-primary btn-sm rounded" @click="onGenerate">
          {{ $t("loginmodal.confirmAndGenerate") }}
        </button>
      </div>
      <div class="h-full overflow-y-auto p-2">
        <image-style-selection v-model:selected-style-id="selectedStyleId" />
      </div>
    </popup>
    <radial-progress-popup
      ref="progressRef"
      v-model:show="showProgress"
      :fallback-text="$t('loginmodal.receiving')"
      :textList="[$t('loginmodal.generating')]"
      :timeout-sec="10"
    />
  </div>
</template>
<style>
@keyframes backgroundAnimation {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 0 100%;
  }
}

@keyframes grayscaleAnimation {
  0% {
    filter: grayscale(0);
  }
  100% {
    filter: grayscale(1);
  }
}

.register-container {
  animation: grayscaleAnimation 0.5s linear;
}

.register-container-bg {
  /** animation: backgroundAnimation 20s linear infinite; **/
}
</style>
